import * as ChoiceApi from "./ChoiceApi"
import { AppScreens, PrivilegeType, AppScreenNames } from '../Enums/ChoiceEnums';
import { popIframe, popAlert} from "./Notification"

let loadedScreenInfo: ChoiceApi.IUserConfigurationScreens[] | undefined = [];   // Avoid using unless you know it has been loaded or are loading it

export interface IWindowSecurity {
    screenId: AppScreens,
    security?: PrivilegeType
}
export function getWindowSecurity(windowSecurity: IWindowSecurity[], screenId: AppScreens) : number {
    const index = windowSecurity.findIndex(o => o.screenId === screenId);
    return +(windowSecurity[index]?.security ?? PrivilegeType.None);
}
export async function setWindowSecurity(windowSecurity: IWindowSecurity[]) {
    /* forEach won't work for async calls... */ 
    for (const [index, element] of windowSecurity.entries()) { 
        let securityJson = await getScreenInfo(element.screenId) as ChoiceApi.IUserConfigurationScreens;  
        windowSecurity[index].security = securityJson?.security ?? PrivilegeType.None;
    }
}
// Is the user allowed to access the specified screen?
export async function haveScreenAccess(screenId: AppScreens | string): Promise<boolean> {
    const matchingScreen = await getScreenInfo(screenId);
    if (!matchingScreen)
        return false;

    // Known / permitted options
    if (PrivilegeType.Read === matchingScreen.security || PrivilegeType.ReadUpdate === matchingScreen.security || PrivilegeType.ReadUpdateWrite === matchingScreen.security)
        return true;

    // Known / no rights
    if (PrivilegeType.None === matchingScreen.security)
        return false;

    // Unknown
    alert("Unknown privilege value");
    throw new Error("Unknown privilege value");
}

// Get info for the specified screen
/*
    In: cw_lookup:
        SQL:
            DELETE FROM security_screens
            WHERE screen_name = 'MY_SCREEN_NAME'

            INSERT INTO security_screens(screen_name, screen_description, height, width, agnostic, screen_id)
            VALUES('MY_SCREEN_NAME', 'MY_SCREEN Description', 768, 985, 'Y', 'MY_SCREEN_ID')


    In: Client Database:
        product_id: From cw_lookup
        role_selection: Look at Office MD => Setup => Roles => "Groups column".
            The first level is the description related to the product_id
            The second level is the role_selection name used

        SQL:
            DELETE FROM security_screens
            WHERE screen_name = 'MY_SCREEN_NAME'

            INSERT INTO security_screens(screen_name, screen_alias, default_screen_indicator, screen_description, product_id, role_section)
            VALUES('MY_SCREEN_NAME', 'MY_SCREEN_ALIAS', 'N', 'MY_SCREEN Description', (An integer), (Varies))

  If copying rights of an existing screen
      
            EXEC dbo.isp_security_screen_copy_roles
                @screen_name         = 'DepositReconcile.htm', -- screen where security being copied from
                @screen_list         = 'depositlisting', -- new screen
                @read_only           = 0
*/
export async function getScreenInfo(screenId: string) : Promise<ChoiceApi.IUserConfigurationScreens | undefined>
{
    const screens = await getScreens();
    return screens.find(scr => scr.screenId?.toLowerCase() === screenId?.toLowerCase()); 
}
// --------------------------------
// Get the screen info for the user
async function getScreens() : Promise<ChoiceApi.IUserConfigurationScreens[]>
{
    if (loadedScreenInfo?.length)
        return loadedScreenInfo;

    // Check session storage?
    const screensJson = sessionStorage.getItem("UserConfiguration_Screens"); 
    if (screensJson)
    {
        loadedScreenInfo = JSON.parse(screensJson);
        if (loadedScreenInfo?.length)
            return loadedScreenInfo;
    }

    // Fetch from server
    return UpdateUserConfigurationScreens();
}
// Re-read the screen data from the API
export async function UpdateUserConfigurationScreens() : Promise<ChoiceApi.IUserConfigurationScreens[]>
{
    loadedScreenInfo = await ChoiceApi.userConfiguration.getScreens();
    if (!Array.isArray(loadedScreenInfo))
    {
        alert("Error getting user configuration screens data");
        return [];
    }

    sessionStorage.setItem("UserConfiguration_Screens", JSON.stringify(loadedScreenInfo));
    return loadedScreenInfo;
}
// Modal Window Opener
interface IWindowOpenerBase {
    title?: string,
    modalClass?: "modal-sm" | "modal-lg" | "modal-xl" | "modal-fullscreen" | "modal-fullscreen-xxl-down"
    heightPercent?: number
}

type UrlParamterDataValues = Record<string,any>;
export interface ModalWindowOpener extends IWindowOpenerBase {
    screenID: AppScreens,
    urlParmData?: UrlParamterDataValues,
    vueModalOnly?: boolean,
    callBack?: () => void
} 

type UrlParameters = Record<string,IParmDefinitions>;
interface IWindowData  extends IWindowOpenerBase { 
    url: string,
    urlParms? : UrlParameters,
    oldStyleParmJoin?: string,
    vueFooter?: boolean,    // False by default
    maxHeight? : number
    // Legacy revert support
    legacyScreenName?: AppScreenNames,  // If want to be able to revert to a legacy screen, what is the screen name?
    legacyUrl?: string,                 // If we're supposed to use the legacy screen, what is the url?
    securityPrivilegeType?: PrivilegeType   // If the privilege is hard coded, we don't need to check how it is setup in the database
}
 
interface IParmDefinitions {
    optional?: boolean,  // Default = required
    type?: "string" | "number",
    valueInNoDataValues?: any   // When calling ModalWindowOpenByName, if no data is passed we rely on this value to set the parameter.
}
// NOTE: Using AppScreens|string so we don't have to add all AppScreen items
const modalWindowList : Record <AppScreens|string, IWindowData> =
 {
    [AppScreens.DepositNew]: {title: "New Deposit", url: "/officemd/screens/deposit_new_v2.htm", urlParms: {DepositID : {type: "number"},PaymentBatchID: {optional: true, type: "number"}}, modalClass: "modal-xl", heightPercent: .95, vueFooter: false, oldStyleParmJoin: "|", maxHeight: 700},
    [AppScreens.Logging]: {title: "Audit Information", url: "/officemd/screens/logging.asp", urlParms: {ScreenName: {type: "string"}, LogType: {optional: true, type: "string"}, StatementId: {type: "string"}}, modalClass: "modal-fullscreen-xxl-down", heightPercent: .8, vueFooter: false, oldStyleParmJoin: "[*]", maxHeight: 740},
    [AppScreens.DocumentScan]: {title: "Document Scan", url: "/officemd/screens/DocumentScan.htm", urlParms: {type: {type: "string"}, eid: {type: "number"}, iid: {type: "number"}}, modalClass: "modal-xl", heightPercent: .9, vueFooter: false, oldStyleParmJoin: "|"},
    [AppScreens.Deposit]: {title: "Deposit Details", url: "/officemd/screens/deposit_v2.htm", urlParms: {DepositID: {type: "number"}}, modalClass: "modal-xl", heightPercent: .8, vueFooter: false, oldStyleParmJoin: "|"},
    [AppScreens.ReceiptDetails]: {title: "Receipt Details", url: "/officemd/screens/receiptdetails.htm", urlParms: {ReceiptID: {type: "number"}}, modalClass: "modal-xl", heightPercent: .8, vueFooter: false, oldStyleParmJoin: "|"},
    [AppScreens.BillerAction]: { title: "Biller Action", 
                                    url: "/officemd/screens/WindowQuery.htm", 
                                    urlParms:{ WN: { type: "string", valueInNoDataValues: "ReferenceTables" }, ID: { type: "number", valueInNoDataValues: 155} }, 
                                    modalClass: "modal-fullscreen", oldStyleParmJoin: "|" },
    [AppScreens.User2FA]: { title: "Login - Two Factor Authentication",
                                    url:"/officemd/App/User/User2FA",
                                    urlParms:{ user_id: { type: "string" }, securityUserId: { type: "number" } }, 
                                    modalClass: "modal-fullscreen",
                                    legacyScreenName: AppScreenNames.User2FA,
                                    legacyUrl: "/officemd/screens/User2FA.htm"},
    [AppScreens.PatientEligibility]: { title: "Patient Eligibility", url: "/officemd/screens/PatientEligibility.asp", urlParms: { EID: { type: "number" } }, modalClass: "modal-xl", heightPercent: .8, vueFooter: false, oldStyleParmJoin: "|" },
    [AppScreens.PatientCostEstimator]: { title: "Cost Estimator", url: "/officemd/screens/vue/PatientCostEstimator.htm", urlParms: { PatientId: { type: "number" } }, modalClass: "modal-xl", heightPercent: .8, vueFooter: false, oldStyleParmJoin: "|" },
    [AppScreens.IdMeSign]: { title: "Controlled Substance Verification", url: "/officemd/App/IdMe/CredentialSign", securityPrivilegeType: PrivilegeType.ReadUpdateWrite },
    [AppScreens.IdmeApprove]: { title: "IDME Approval", url: "/officemd/App/IdMe/CredentialApprove", securityPrivilegeType: PrivilegeType.ReadUpdateWrite },
    [AppScreens.ChoiceAudit]: { title: "Choice Audit", url: "/officemd/app/audit/ChoiceAudit", urlParms: { identifierId: { type: "number" }, screenName: { type: "string"} }, modalClass: "modal-xl", heightPercent: .8, vueFooter: false },
    [AppScreens.CreditRules]: { title: "Credit Rules", url: "/officemd/screens/MultiCompanySetting.htm", urlParms: { PARM: { type: "string" } }, modalClass: "modal-xl", heightPercent: .75, vueFooter: false },    
    [AppScreens.PaymentPlanRules]: { title: "Payment Plan Rules", url: "/officemd/screens/MultiCompanySetting.htm", urlParms: { PARM: { type: "string" } }, modalClass: "modal-xl", heightPercent: .75, vueFooter: false },    
    [AppScreens.MMLAdmin]: { title: "MyMedicalLocker Admin", 
                                urlParms: { WindowID: { type: "number" }, Qualifier: { type: "string", valueInNoDataValues: "entity"}},
                                url: "/officemd/screens/mmlkey.htm",
                                modalClass: "modal-xl",
                                legacyScreenName: AppScreenNames.MMLAdmin,
                                legacyUrl: "/officemd/screens/mmlkey.htm" },
    [AppScreens.PatientHeaderSetup]: { 
        title: "Patient Header Setup", 
        url: "/officemd/App/Patient/PatientHeaderSetup", 
        urlParms: { patientEid: { type: "number", optional: false} },
        securityPrivilegeType: PrivilegeType.ReadUpdateWrite }
 };

// Open a legacy OfficeMD screen as a modal window.  
export async function ModalWindowOpenByName(screen : AppScreens) : Promise<void>
{
    return ModalWindowOpen({ screenID: AppScreens.BillerAction });
}

export async function ModalWindowOpen(newModalWindow : ModalWindowOpener) : Promise<void>
{
    /*
     If isTop is true use vue Modal object else use window open request from parent window (for now) 
     Use the windowList record (above) when adding new screens...
     */
     const isTop = (window === window.top);
    if ((isTop) || (newModalWindow.vueModalOnly)) { 

        const windowData : IWindowData | undefined = modalWindowList[newModalWindow.screenID];
        if (!windowData) {
            await popAlert((newModalWindow.screenID ?? "") + " is not found in the windowList interface record","Invalid Window Opener"); 
            return;
        }
    
        // Do we need to add URL data if it wasn't supplied?
        if (undefined === newModalWindow.urlParmData && undefined !== windowData.urlParms && undefined !== typeof(windowData.urlParms))
        {
            newModalWindow.urlParmData = {};
            for(const urlParam in windowData.urlParms)
                newModalWindow.urlParmData[urlParam] = windowData.urlParms[urlParam].valueInNoDataValues;
        }    

        // Build URL  
        const url_parm_data = buildURLParmString(windowData.urlParms, newModalWindow.urlParmData);
        const iframe_source = windowData.url + (url_parm_data === "" ? "" : "?" + url_parm_data);
        await popIframe(iframe_source,
            newModalWindow.title ?? windowData.title,
            newModalWindow.modalClass ?? windowData.modalClass, 
            newModalWindow.heightPercent ?? windowData.heightPercent,
            windowData.vueFooter ?? false,
            windowData.maxHeight ?? 0);  
        if (newModalWindow.callBack) {
            newModalWindow?.callBack();
        }            
    }
    else {
        return await LegacyModalWindowOpen(newModalWindow);
    } 
}

// Open a legacy window -- it should only be called from ModalWindow.ts or ChoiceUserConfiguration.ts
export async function LegacyModalWindowOpen(newModalWindow : ModalWindowOpener) : Promise<void>
{
    const windowData : IWindowData | undefined = modalWindowList[newModalWindow.screenID];
    if (!windowData) {
        await popAlert((newModalWindow.screenID ?? "") + " is not found in the windowList interface record","Invalid Window Opener"); 
        return;
    }

    let adjusted_screen_id = newModalWindow.screenID.toString();
    const url_parm_data = buildURLParmString(windowData.urlParms, newModalWindow.urlParmData, windowData.oldStyleParmJoin, true);
    switch (newModalWindow.screenID) {
        case AppScreens.DocumentScan:
            await popAlert("Unable to open window",(modalWindowList as any)[newModalWindow.screenID].title);
            break;
        case AppScreens.DepositNew: // Override screen id...
            adjusted_screen_id = newModalWindow.screenID  + "pb";
            break; 
    }

    if (!(parent as any).window_open_request)
    {
        await popAlert("Window does not include the window.js script");
        return;
    }

    (parent as any).window_open_request(adjusted_screen_id,url_parm_data,"",true,"");    
}

// This method forms URLs for specific screens.
//  If data is needed, it can be specified via the urlParmData collection
export function modalWindowUrl(newModalWindow : ModalWindowOpener): string|undefined
{
    const windowData : IWindowData | undefined = modalWindowList[newModalWindow.screenID];
    if (!windowData) {
        popAlert((newModalWindow.screenID ?? "") + " is not found in the windowList interface record","Invalid Window Opener"); 
        return;
    }

    const isTop = (window === window.top);
    if ((isTop) || (newModalWindow.vueModalOnly)) { 
        const url_parm_data = buildURLParmString(windowData.urlParms, newModalWindow.urlParmData);
        const iframe_source = windowData.url + (url_parm_data === "" ? "" : "?" + url_parm_data);
        return iframe_source;
    }
    else
    {
        return buildURLParmString(windowData.urlParms, newModalWindow.urlParmData, windowData.oldStyleParmJoin, true);
    }
}

function buildURLParmString(urlParms?: UrlParameters, urlParmData?: UrlParamterDataValues, dataJoiner?: string, dataOnly: boolean = false): string {
    if (!urlParms) 
        return "";
    if (dataOnly) {
        dataJoiner = dataJoiner ?? "|";
        return Object.keys(urlParms).map((item) =>  (!urlParmData ? "" : urlParmData[item])).join(dataJoiner) ?? ""; 
    }
    else {
        dataJoiner = dataJoiner ?? "&";
        return Object.keys(urlParms).map((item) => item + "=" + (!urlParmData ? "" : urlParmData[item])).join(dataJoiner) ?? "";
    }
}

export function windowInfo(screen: AppScreens): IWindowData | undefined
{
    return modalWindowList[screen];
}